import type { PDFDocumentProxy } from 'pdfjs-dist';
import { configurePdfWorker, pdfjsLib } from './pdfjs';

configurePdfWorker();

export type LoadedDocument =
  | {
      kind: 'pdf';
      pdf: PDFDocumentProxy;
      pageCount: number;
      fileHash: string;
      fileName: string;
      data: Uint8Array;
      /** transient = true → не загружать сохранённую разметку и не сохранять текущую (для библиотеки) */
      transient?: boolean;
    }
  | {
      kind: 'image';
      imageBitmap: ImageBitmap;
      width: number;
      height: number;
      fileHash: string;
      fileName: string;
      transient?: boolean;
    }
  | {
      // PDF, растеризованный на сервере (через pdftoppm) в PNG страницы.
      // Используется для библиотечных файлов — гарантирует pixel-perfect рендер
      // ивритских/CJK/Type1C шрифтов, с которыми pdf.js не справляется.
      kind: 'rasterized-pdf';
      pageCount: number;
      pageUrls: string[];
      fileHash: string;
      fileName: string;
      transient?: boolean;
    };

export class UnsupportedFileError extends Error {
  constructor() {
    super('UNSUPPORTED_FILE_TYPE');
    this.name = 'UnsupportedFileError';
  }
}

export async function loadDocument(file: File): Promise<LoadedDocument> {
  const fileHash = await hashFile(file);
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  if (isPdf) {
    const buf = await file.arrayBuffer();
    const data = new Uint8Array(buf);
    // cMaps + standard_fonts + useSystemFonts критичны для иврита/CJK/сложных шрифтов.
    // Без них pdf.js не может декодировать глифы — текст превращается в кашу.
    const baseUrl = new URL('./', window.location.href).href;
    const pdf = await pdfjsLib.getDocument({
      data: data.slice(),
      cMapUrl: baseUrl + 'cmaps/',
      cMapPacked: true,
      standardFontDataUrl: baseUrl + 'standard_fonts/',
      useSystemFonts: true,
      disableFontFace: false,
    }).promise;
    return {
      kind: 'pdf',
      pdf,
      pageCount: pdf.numPages,
      fileHash,
      fileName: file.name,
      data,
    };
  }
  if (file.type.startsWith('image/')) {
    const imageBitmap = await createImageBitmap(file);
    return {
      kind: 'image',
      imageBitmap,
      width: imageBitmap.width,
      height: imageBitmap.height,
      fileHash,
      fileName: file.name,
    };
  }
  throw new UnsupportedFileError();
}

async function hashFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .slice(0, 16)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
